import type { MeResponse as Me, Permission } from '@ops-desk/contracts';
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, isApiError } from '@/lib/api.ts';

/** `forbidden`: signed in, but the account lacks the role required to use the app. */
export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'forbidden' | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  /** Present only while `status === 'authenticated'`. Always fetched fresh, never cached. */
  me: Me | null;
  /** Full-page navigation to the server-side login flow, returning to `returnTo` afterwards. */
  login: (returnTo?: string) => void;
  logout: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  /** Re-run the boot request (used by the "try again" action on `error`). */
  retry: () => void;
  /**
   * Why the last sign-in was refused (`?authError=` from the API callback redirect), captured once
   * at boot before the router can redirect it away; e.g. `role` when the account lacks access.
   */
  authError: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The three helpers below are the only places that know the auth endpoints, so switching
// logout to a GET redirect (or changing the login URL) is a one-line change.

export function loginUrl(returnTo: string): string {
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

export function fetchMe(signal?: AbortSignal): Promise<Me> {
  return api.get<Me>('/api/auth/me', signal ? { signal } : {});
}

export async function requestLogout(): Promise<void> {
  await api.post<void>('/api/auth/logout');
}

/** Reads and strips `?authError=` from the current URL (so it is not carried into returnTo). */
function takeAuthError(): string | null {
  const url = new URL(window.location.href);
  const error = url.searchParams.get('authError');
  if (error !== null) {
    url.searchParams.delete('authError');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
  return error;
}

/** Only same-origin relative paths are valid return targets. */
function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

function sanitizeReturnTo(value: string): string {
  return value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export interface AuthProviderProps {
  children: ReactNode;
  /** Full-page navigation. Injectable for tests. */
  navigate?: (url: string) => void;
}

export function AuthProvider({
  children,
  navigate = (url) => window.location.assign(url),
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [authError] = useState(takeAuthError);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` re-triggers the boot request
  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchMe(controller.signal).then(
      (result) => {
        setMe(result);
        setStatus('authenticated');
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setMe(null);
        setStatus(
          !isApiError(error)
            ? 'error'
            : error.status === 401
              ? 'anonymous'
              : error.status === 403
                ? 'forbidden'
                : 'error',
        );
      },
    );
    return () => controller.abort();
  }, [attempt]);

  const login = useCallback(
    (returnTo?: string) => navigate(loginUrl(sanitizeReturnTo(returnTo ?? currentPath()))),
    [navigate],
  );

  const logout = useCallback(async () => {
    try {
      await requestLogout();
    } finally {
      // Whatever the outcome, drop all client state with a full navigation to the app root.
      navigate('/');
    }
  }, [navigate]);

  const hasPermission = useCallback(
    (permission: Permission) => me?.permissions.includes(permission) ?? false,
    [me],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, me, login, logout, hasPermission, retry, authError }),
    [status, me, login, logout, hasPermission, retry, authError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
